import { Fix64 } from "../maths/fix64";

/**
 * M36 spawn rules (docs/RARES.md), parsed from `enemies.json` → `immunity` and `rareRules`. Both default to "off", so a
 * bundle (or a test fixture) without them runs exactly as before.
 */

/** Immunity bits on a spawned enemy (`Enemy.immunity`, `SpawnEntry.immunity`): physical, magic, fire (burn and fire damage), slows. */
export const Immunity = { None: 0, Physical: 1, Magic: 2, Fire: 4, Slow: 8 } as const;
export type ImmunityKind = 1 | 2 | 4 | 8;
/** Data ids of the immunities, in bit order (enemies.json `immunity.kinds`). */
export const IMMUNITY_IDS = ["physical", "magic", "fire", "slow"] as const;

/**
 * Immune units (Kevin 2026-10-10: "occasional units immune to certain types of damage to force rotation of towers"):
 * from `fromLevel`, about one stage in `everyStages` (from `fromStage`) makes `countMin`–`countMax` of its ordinary
 * spawns of one archetype immune to one kind, drawn by `kindWeights` (bit order). Bosses, rares and elites never are.
 */
export interface ImmunityRules {
  readonly fromLevel: number;
  readonly fromStage: number;
  readonly everyStages: number;
  readonly countMin: number;
  readonly countMax: number;
  /** Weight per immunity kind, in IMMUNITY_IDS order. */
  readonly kindWeights: readonly number[];
  /** An immune unit's HP against a plain spawn of its archetype (the retune that keeps the win-rate targets). */
  readonly hpMult: Fix64;
}

/**
 * Rare spawns (Kevin 2026-10-10, "shiny" creatures; M37: Iron and Endless too, sim/rares.ts): on a level from `fromLevel`, at a stage's start, from `fromStage`, never the last (boss) stage,
 * `chancePerMillion` / 1,000,000 that the stage is one rare creature of the level's chapter instead of its waves.
 * Its HP is the level's boss at that stage (boss HP multiplier × the stage's power × the boss archetype's HP) × `hpMult`.
 * `offerDelayTicks` after it appears, `offers` tower/upgrade-only card offers open back to back. Killed, it pays a chest
 * `chestRarityStep` rarities above the level's battle chest for a win (capped at Mythic).
 */
export interface RareRules {
  /** First campaign level a rare can appear on (the tutorial level has none). */
  readonly fromLevel: number;
  readonly chancePerMillion: number;
  readonly fromStage: number;
  readonly hpMult: Fix64;
  readonly offerDelayTicks: number;
  readonly offers: number;
  readonly chestRarityStep: number;
  /** Endless (M37): waves from one rare's wave before the next may roll (several a run, never two close together). */
  readonly endlessCooldownStages: number;
  /** Iron (M37): × on the rare's HP there (its blocks follow on without a break, so the same creature is a harder kill). */
  readonly ironHpMult: Fix64;
}

/**
 * The Starfall Wyrm (M40, docs/RARES.md, sim/wyrm.ts): wherever a rare may roll (from `fromLevel`, stages from
 * `fromStage`), an independent `chancePerMillion` / 1,000,000 draw makes the stage ONE Wyrm, at most once a run. Its HP
 * is `powerFactor` (by chapter; Iron and Endless their own) × the damage the board can deal it on one pass, never below
 * `floorMult` × the HP a rare would have at that stage (sim/wyrmPower.ts). A retry after it flies off costs `retryGems`; slain with the Starfall set
 * complete, it pays a Mythic chest and `completeSetGems`. Its rally: `offers` offers `offerDelayTicks` after it appears.
 */
export interface WyrmRules {
  readonly fromLevel: number;
  readonly chancePerMillion: number;
  readonly fromStage: number;
  /** × one pass's damage on a campaign level, by its chapter (1-based; past the last, the last): calibrated per chapter. */
  readonly powerFactor: readonly Fix64[];
  /** The same in Iron (its blocks follow on without a break) and Endless. */
  readonly ironPowerFactor: Fix64;
  readonly endlessPowerFactor: Fix64;
  readonly floorMult: Fix64;
  readonly offerDelayTicks: number;
  readonly offers: number;
  readonly retryGems: number;
  /** A retry brings it back wounded, with the HP it fled with (the lead's call, 2026-10-12); false: at full HP. */
  readonly retryKeepsDamage: boolean;
  readonly completeSetGems: number;
}

export interface SpawnRulesData { readonly immunity: ImmunityRules; readonly rare: RareRules; readonly wyrm: WyrmRules }

/** One in a million, the rare chance's resolution. */
export const RARE_CHANCE_SCALE = 1_000_000;

export function emptySpawnRules(): SpawnRulesData {
  return {
    immunity: { fromLevel: Number.MAX_SAFE_INTEGER, fromStage: 1, everyStages: 1, countMin: 0, countMax: 0, kindWeights: IMMUNITY_IDS.map(() => 0), hpMult: Fix64.One },
    rare: { fromLevel: Number.MAX_SAFE_INTEGER, chancePerMillion: 0, fromStage: 1, hpMult: Fix64.One, offerDelayTicks: 0, offers: 0, chestRarityStep: 0, endlessCooldownStages: 0, ironHpMult: Fix64.One },
    wyrm: { fromLevel: Number.MAX_SAFE_INTEGER, chancePerMillion: 0, fromStage: 1, powerFactor: [Fix64.One], ironPowerFactor: Fix64.One, endlessPowerFactor: Fix64.One, floorMult: Fix64.Zero, offerDelayTicks: 0, offers: 0, retryGems: 0, retryKeepsDamage: false, completeSetGems: 0 },
  };
}

/** JSON parsing boundary (CLAUDE.md: no `any` outside it). */
type Json = any;

function opt(o: Json, key: string, fallback: Json): Json { return o !== null && typeof o === "object" && key in o ? o[key] : fallback; }
function bool(v: Json, where: string): boolean {
  if (typeof v !== "boolean") throw new Error(`${where}: expected true or false`);
  return v;
}
function int(v: Json, where: string): number {
  if (typeof v !== "number" || !Number.isInteger(v) || v < 0) throw new Error(`${where}: expected a non-negative integer`);
  return v;
}

/** A probability string as parts per million, rounded (0.005 is not exact in Q32.32). */
function perMillion(v: Json): number { return Fix64.parse(String(v)).mulInt(RARE_CHANCE_SCALE).add(Fix64.fromFraction(1, 2)).floorToInt(); }

/** Reads `enemies.wyrmRules` (M40; absent: off). */
function parseWyrmRules(w: Json, off: WyrmRules): WyrmRules {
  if (w === null) return off;
  const fix = (v: Json): Fix64 => Fix64.parse(String(v));
  const pf = opt(w, "powerFactor", "1"), factors = (Array.isArray(pf) ? pf : [pf]).map(fix);
  if (factors.length === 0) throw new Error("wyrmRules.powerFactor: at least one factor");
  return {
    fromLevel: int(opt(w, "fromLevel", 1), "wyrmRules.fromLevel"), chancePerMillion: perMillion(opt(w, "wyrmChance", "0")),
    fromStage: int(opt(w, "fromStage", 3), "wyrmRules.fromStage"),
    powerFactor: factors, ironPowerFactor: fix(opt(w, "ironPowerFactor", "1")), endlessPowerFactor: fix(opt(w, "endlessPowerFactor", "1")), floorMult: fix(opt(w, "floorMult", "0")),
    offerDelayTicks: int(opt(w, "offerDelayTicks", 60), "wyrmRules.offerDelayTicks"), offers: int(opt(w, "offers", 3), "wyrmRules.offers"),
    retryGems: int(opt(w, "retryGems", 0), "wyrmRules.retryGems"), retryKeepsDamage: bool(opt(w, "retryKeepsDamage", false), "wyrmRules.retryKeepsDamage"), completeSetGems: int(opt(w, "completeSetGems", 0), "wyrmRules.completeSetGems"),
  };
}

/** Reads `enemies.immunity`, `enemies.rareRules` and `enemies.wyrmRules` from the bundle root (absent: off). */
export function parseSpawnRules(root: Json): SpawnRulesData {
  const e = opt(root, "enemies", null), empty = emptySpawnRules();
  const im = opt(e, "immunity", null), rr = opt(e, "rareRules", null);
  const immunity: ImmunityRules = im === null ? empty.immunity : {
    fromLevel: int(opt(im, "fromLevel", 1), "immunity.fromLevel"), fromStage: int(opt(im, "fromStage", 1), "immunity.fromStage"),
    everyStages: Math.max(1, int(opt(im, "everyStages", 4), "immunity.everyStages")),
    countMin: int(opt(im, "countMin", 2), "immunity.countMin"), countMax: int(opt(im, "countMax", 5), "immunity.countMax"),
    kindWeights: IMMUNITY_IDS.map((id) => int(opt(opt(im, "kinds", {}), id, 0), `immunity.kinds.${id}`)),
    hpMult: Fix64.parse(String(opt(im, "hpMult", "1"))),
  };
  if (immunity.countMax < immunity.countMin) throw new Error("immunity: countMax < countMin");
  const rare: RareRules = rr === null ? empty.rare : {
    fromLevel: int(opt(rr, "fromLevel", 1), "rareRules.fromLevel"),
    chancePerMillion: perMillion(opt(rr, "rareChance", "0")),
    fromStage: int(opt(rr, "fromStage", 3), "rareRules.fromStage"),
    hpMult: Fix64.parse(String(opt(rr, "hpMult", "1"))),
    offerDelayTicks: int(opt(rr, "offerDelayTicks", 60), "rareRules.offerDelayTicks"),
    offers: int(opt(rr, "offers", 3), "rareRules.offers"),
    chestRarityStep: int(opt(rr, "chestRarityStep", 2), "rareRules.chestRarityStep"),
    endlessCooldownStages: int(opt(rr, "endlessCooldownStages", 5), "rareRules.endlessCooldownStages"),
    ironHpMult: Fix64.parse(String(opt(rr, "ironHpMult", "1"))),
  };
  return { immunity, rare, wyrm: parseWyrmRules(opt(e, "wyrmRules", null), empty.wyrm) };
}

/** Bit of an immunity id ("physical" → 1 …). */
export function immunityBit(id: string): number {
  const i = (IMMUNITY_IDS as readonly string[]).indexOf(id);
  return i < 0 ? 0 : 1 << i;
}
